import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationSystem from './NotificationSystem';
import chatService from '../services/chatService';
import './BottomNavigationBar.css';

const BottomNavigationBar = ({ 
  onHomeClick, 
  onLoginClick, 
  onSignupClick, 
  onProfileClick,
  onAccountSettingsClick,
  onJobsClick,
  onMessagesClick,
  onNotificationsClick,
  onForumClick
}) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  
  // Determine active route
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '';
    }
    return location.pathname.startsWith(path);
  };

  // Fetch unread message count only on login
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setUnreadMessageCount(0);
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        const response = await chatService.getUnreadCounts();
        if (response.success && response.data) {
          // Sum all unread counts from all rooms
          const totalUnread = Object.values(response.data).reduce((sum, count) => sum + (count || 0), 0);
          setUnreadMessageCount(totalUnread);
        }
      } catch (error) {
        console.error('Error fetching unread message count:', error);
      }
    };

    // Fetch only once when user logs in
    fetchUnreadCount();

    // Listen for unread count updates via Pusher
    const handleUnreadCountUpdate = (event) => {
      const data = event.detail;
      if (data) {
        if (data.total_unread !== undefined) {
          setUnreadMessageCount(data.total_unread);
        } else if (data.unread_counts) {
          // Calculate total from unread_counts object
          const totalUnread = Object.values(data.unread_counts).reduce((sum, count) => sum + (count || 0), 0);
          setUnreadMessageCount(totalUnread);
        }
      }
    };

    window.addEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);

    return () => {
      window.removeEventListener('pusher:chat-unread-count-updated', handleUnreadCountUpdate);
    };
  }, [isAuthenticated, user?.id]); // Only re-run when login status or user ID changes

  return (
    <nav className="bottom-navigation-bar">
      {!isAuthenticated ? (
        // Guest user buttons
        <div className="bottom-nav-guest">
          <button 
            className="bottom-nav-btn login-btn"
            onClick={onLoginClick}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
              <polyline points="10 17 15 12 10 7"></polyline>
              <line x1="15" y1="12" x2="3" y2="12"></line>
            </svg>
            <span>Login</span>
          </button>
          <button 
            className="bottom-nav-btn signup-btn"
            onClick={onSignupClick}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <line x1="19" y1="8" x2="19" y2="14"></line>
              <line x1="22" y1="11" x2="16" y2="11"></line>
            </svg>
            <span>Sign Up</span>
          </button>
        </div>
      ) : (
        // Logged-in user buttons
        <div className="bottom-nav-authenticated">
          <button 
            className={`bottom-nav-btn ${isActive('/') ? 'active' : ''}`}
            onClick={onHomeClick}
            title="Home"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9,22 9,12 15,12 15,22"></polyline>
            </svg>
            <span>Home</span>
          </button>
          
          <button 
            className={`bottom-nav-btn ${isActive('/categories') || isActive('/vacancies') || isActive('/my-jobs') ? 'active' : ''}`}
            onClick={onJobsClick || (() => {
              window.location.href = '/vacancies';
            })}
            title="Jobs"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
            <span>Jobs</span>
          </button>
          
          <button 
            className={`bottom-nav-btn ${isActive('/chat') ? 'active' : ''}`}
            onClick={onMessagesClick || (() => navigate('/chat'))}
            title="Chat"
            style={{ position: 'relative' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            {unreadMessageCount > 0 && location.pathname !== '/chat' && (
              <span className="notification-badge chat-badge" style={{ position: 'absolute', top: '2px', right: '2px' }}>
                {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
              </span>
            )}
            <span>Chat</span>
          </button>
          
          <div className={`bottom-nav-notification-wrapper ${isActive('/notifications') ? 'active' : ''}`}>
            <NotificationSystem isBottomNav={true} />
          </div>
          
          <button 
            className={`bottom-nav-btn profile-btn-bottom ${isActive('/profile') || isActive('/settings') ? 'active' : ''}`}
            onClick={onProfileClick}
            title="Profile"
          >
            <div className={`profile-icon-bottom ${isActive('/profile') || isActive('/settings') ? 'active' : ''}`}>
              {user?.profile_photo_url ? (
                <img src={user.profile_photo_url} alt={user.name} />
              ) : (
                <div className="default-avatar-bottom">
                  <span className="avatar-initial-bottom">
                    {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                  </span>
                </div>
              )}
            </div>
            <span>Profile</span>
          </button>
        </div>
      )}
    </nav>
  );
};

export default BottomNavigationBar;
